import { useState, useCallback, useRef, useEffect } from 'react';
import { useIdControlCapabilities } from './IdControlProvider';
import { IdPreviewPopover } from './IdPreviewPopover';
import { IdJsonModal } from './IdJsonModal';
import type { ContainerKind } from './types';
import './IdControl.css';
import { appHref } from '../../tenant';

export interface IdControlProps {
  /** The full ID value — always copied to clipboard regardless of `display`. */
  value: string;
  /**
   * Logical container kind. Drives which menu items light up.
   * If omitted, the control behaves as today's CopyId (click = copy, no menu).
   */
  container?: ContainerKind;
  /** Optional short label shown instead of full UUID (still copies full value). */
  display?: string;
  className?: string;
}

interface MenuPos {
  left: number;
  top: number;
  bottom: number;
  /** True when opened by right-click; we use the cursor position rather than the trigger rect. */
  fromCursor: boolean;
}

/**
 * Click ID body → copy to clipboard.
 * Click ⋮ icon (or right-click) → open menu with capabilities provided by host app.
 * Falls back to copy-only when no provider is mounted or no `container` is set.
 */
export function IdControl({ value, container, display, className }: IdControlProps) {
  const caps = useIdControlCapabilities();
  const [toast, setToast] = useState<{ x: number; y: number; text: string } | null>(null);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  // Determine which menu items are available
  const detailRoute = container && caps.detailRoute ? caps.detailRoute(container, value) : null;
  const canPreview = !!(container && caps.preview);
  const canJson = !!(container && caps.viewJson);
  const canExplorer = !!(container && caps.openExplorer);
  const canRefs = !!(container && caps.findReferences);
  const canCopyUrl = !!(container && detailRoute && caps.detailUrlBase);
  const hasAnyMenuAction = canPreview || canJson || canExplorer || canRefs || !!detailRoute;

  // Without a container or any capabilities beyond copy, behave like the old CopyId.
  // Copy-short and Copy-URL are degenerate without a container, so even the menu would
  // only have one item ("Copy ID"). Skip rendering the trigger entirely in that case.
  const showMenuTrigger = !!container;

  const copy = useCallback((text: string, e: { clientX: number; clientY: number }, label: string) => {
    navigator.clipboard.writeText(text);
    setToast({ x: e.clientX, y: e.clientY, text: label });
    setTimeout(() => setToast(null), 1500);
  }, []);

  const handlePillClick = useCallback((e: React.MouseEvent) => {
    // Default action: show preview if the host can provide one. Otherwise fall back to copy.
    if (canPreview) {
      setShowPreview(true);
    } else {
      copy(value, e, 'Copied');
    }
  }, [canPreview, copy, value]);

  const openMenuFromTrigger = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({ left: rect.left, top: rect.top, bottom: rect.bottom, fromCursor: false });
  }, []);

  const handleIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    openMenuFromTrigger();
  }, [openMenuFromTrigger]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!hasAnyMenuAction && !container) return; // Let browser handle it
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY, bottom: e.clientY, fromCursor: true });
  }, [hasAnyMenuAction, container]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Match click behavior: preview if available, else copy.
      if (canPreview) {
        setShowPreview(true);
      } else {
        const rect = wrapRef.current?.getBoundingClientRect();
        navigator.clipboard.writeText(value);
        if (rect) {
          setToast({ x: rect.left + rect.width / 2, y: rect.top, text: 'Copied' });
          setTimeout(() => setToast(null), 1500);
        }
      }
    } else if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
      e.preventDefault();
      openMenuFromTrigger();
    }
  }, [canPreview, value, openMenuFromTrigger]);

  const closeMenu = useCallback(() => setMenuPos(null), []);

  // Close menu on outside click / scroll / Escape
  useEffect(() => {
    if (!menuPos) return;
    const handleDocClick = () => closeMenu();
    const handleScroll = () => closeMenu();
    const handleKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') closeMenu(); };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuPos, closeMenu]);

  const shortId = value.length > 8 ? `…${value.slice(-8)}` : value;
  const detailUrl = canCopyUrl && detailRoute ? `${caps.detailUrlBase}${detailRoute}` : null;

  // ── Menu actions ────────────────────────────────────────────────────────
  const fireCopy = (text: string, label: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    copy(text, e, label);
    closeMenu();
  };

  const fireNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    if (!detailRoute) return;
    // Open detail pages in a new tab so the current context (submission detail,
    // hint detail, etc.) is preserved. Falls back to in-app nav when no absolute
    // URL can be built (detailUrlBase unset).
    if (detailUrl) {
      window.open(detailUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (caps.navigate) {
      caps.navigate(detailRoute);
    } else {
      window.location.assign(appHref(detailRoute));
    }
  };

  const firePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    setShowPreview(true);
  };

  const fireJson = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    setShowJson(true);
  };

  const fireExplorer = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    if (container && caps.openExplorer) caps.openExplorer(container, value);
  };

  const fireRefs = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    if (container && caps.findReferences) caps.findReferences(container, value);
  };

  return (
    <>
      <span
        ref={wrapRef}
        className={`id-control ${className || ''}`}
        onContextMenu={handleContextMenu}
      >
        <span
          className="id-control-pill"
          onClick={handlePillClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          title={canPreview ? 'Click to preview' : 'Click to copy'}
        >
          {display || shortId}
        </span>
        {showMenuTrigger && (
          <button
            type="button"
            className="id-control-trigger"
            onClick={handleIconClick}
            aria-label="Open ID actions menu"
            title="More actions"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
        )}
      </span>

      {toast && (
        <div
          className="id-control-toast"
          style={{ left: toast.x, top: toast.y, transform: 'translate(-50%, -120%)' }}
        >
          {toast.text}
        </div>
      )}

      {menuPos && (
        <IdMenu
          pos={menuPos}
          onClose={closeMenu}
          sections={[
            {
              label: 'Copy',
              items: [
                { label: 'Copy ID', onClick: fireCopy(value, 'Copied') },
                { label: `Copy short ID (${shortId})`, onClick: fireCopy(shortId.replace(/^…/, ''), 'Copied short') },
                ...(detailUrl ? [{ label: 'Copy URL', onClick: fireCopy(detailUrl, 'Copied URL') }] : []),
              ],
            },
            ...(detailRoute || canExplorer || canRefs ? [{
              label: 'Navigate away',
              items: [
                ...(detailRoute ? [{ label: 'Open detail page', onClick: fireNavigate, navigate: true }] : []),
                ...(canExplorer ? [{ label: 'Open in Explorer', onClick: fireExplorer, navigate: true }] : []),
                ...(canRefs ? [{ label: 'Find references', onClick: fireRefs, navigate: true }] : []),
              ],
            }] : []),
            ...(canPreview || canJson ? [{
              label: 'Inspect',
              items: [
                ...(canPreview ? [{ label: 'Preview', onClick: firePreview }] : []),
                ...(canJson ? [{ label: 'View full JSON', onClick: fireJson }] : []),
              ],
            }] : []),
          ]}
        />
      )}

      {showPreview && container && caps.preview && (
        <IdPreviewPopover
          container={container}
          id={value}
          fetchPreview={caps.preview}
          onViewJson={canJson ? () => { setShowPreview(false); setShowJson(true); } : undefined}
          anchorRect={
            wrapRef.current
              ? {
                  left: wrapRef.current.getBoundingClientRect().left,
                  top: wrapRef.current.getBoundingClientRect().top,
                  bottom: wrapRef.current.getBoundingClientRect().bottom,
                }
              : { left: 0, top: 0, bottom: 0 }
          }
        />
      )}
      {showPreview && (
        <div className="id-control-preview-backdrop" onClick={() => setShowPreview(false)} />
      )}

      {showJson && container && caps.viewJson && (
        <IdJsonModal
          isOpen={showJson}
          container={container}
          id={value}
          onClose={() => setShowJson(false)}
          fetchJson={caps.viewJson}
          renderJson={caps.renderJson}
        />
      )}
    </>
  );
}

// ── Internal menu component ────────────────────────────────────────────────

interface MenuItem {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  /** True for items that navigate away — gets an external-link arrow indicator. */
  navigate?: boolean;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

function IdMenu({ pos, onClose, sections }: { pos: MenuPos; onClose: () => void; sections: MenuSection[] }) {
  // Position: prefer below-right of anchor; flip if it overflows.
  const totalItems = sections.reduce((n, s) => n + s.items.length, 0);
  const MENU_WIDTH = 240;
  const MENU_HEIGHT_ESTIMATE = totalItems * 30 + sections.length * 22 + 12;
  const left = Math.min(pos.left, window.innerWidth - MENU_WIDTH - 8);
  const top = pos.bottom + MENU_HEIGHT_ESTIMATE > window.innerHeight && pos.top > MENU_HEIGHT_ESTIMATE
    ? pos.top - MENU_HEIGHT_ESTIMATE - 4
    : pos.bottom + 4;

  return (
    <div
      className="id-control-menu"
      style={{ left, top, width: MENU_WIDTH }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      role="menu"
    >
      {sections.map((section, si) => (
        <div key={si} className="id-control-menu-section">
          {si > 0 && <div className="id-control-menu-divider" />}
          <div className="id-control-menu-section-label">{section.label}</div>
          {section.items.map((item, i) => (
            <button
              key={i}
              type="button"
              className="id-control-menu-item"
              onClick={(e) => { item.onClick(e); onClose(); }}
              role="menuitem"
            >
              <span>{item.label}</span>
              {item.navigate && (
                <svg
                  className="id-control-menu-item-arrow"
                  width="11" height="11" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M7 17L17 7" />
                  <path d="M7 7h10v10" />
                </svg>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
